import cn from "classnames";
import { Heading } from "components/UI/typography/Heading";
import s from "./SampleCard.module.scss";
import moment from "moment";
import st from "@/styles/modules/typography.module.scss";
import { Link } from "@/components/shared/Link";
import { SpriteIcon } from "@/components/UI/SpriteIcon";
import IconTurnDownRight from "public/img/icons/arrow-turn-down-right.svg";
import { useSampleFilterContext } from "@/components/SampleFilterContext";
import { escapeRegExp } from "lodash";
import { useEffect, useRef, useState } from "react";

type SampleCardCategory = {
  title: string;
  slug: string;
};

export type SampleCardProps = {
  id: number;
  heading: string;
  category: SampleCardCategory;
  date: string;
  words: number;
  pages: number;
  views: number;
  excerpt: string;
  slug: string;
  className?: string;
  headingTag?: 2 | 3;
};

export const SampleCard = ({
  heading,
  category,
  date,
  words,
  pages,
  views,
  excerpt,
  slug,
  headingTag,
  className = "",
}: SampleCardProps) => {
  const context = useSampleFilterContext();
  const cardRef = useRef<HTMLDivElement>(null);

  const [hoverFromBottom, setHoverFromBottom] = useState(false);

  const title = context?.selectedValues?.title;

  const displayHeading = title
    ? heading.replace(
        new RegExp(escapeRegExp(String(title)), "gi"),
        (fragment) => `<span class="match">${fragment}</span>`
      )
    : heading;

  useEffect(() => {
    if (!cardRef.current) return;

    const ref = cardRef.current;

    const watchEnterLeave = (event: MouseEvent, leave: boolean) => {
      const { clientY } = event;
      const { top, height } = ref.getBoundingClientRect();
      const isBelowHeight = clientY - top > height / 2;
      setHoverFromBottom(leave ? !isBelowHeight : isBelowHeight);
    };

    const watchEnter = (event: MouseEvent) => {
      watchEnterLeave(event, false);
    };

    const watchLeave = (event: MouseEvent) => {
      watchEnterLeave(event, true);
    };

    ref.addEventListener("mouseenter", watchEnter);
    ref.addEventListener("mouseleave", watchLeave);

    return () => {
      ref.removeEventListener("mouseenter", watchEnter);
      ref.removeEventListener("mouseleave", watchLeave);
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className={cn(
        st.card,
        s.sampleCard,
        {
          [s.hoverFromTop]: !hoverFromBottom,
          [s.hoverFromBottom]: hoverFromBottom,
        },
        className
      )}
    >
      <div>
        <div className={s.top}>
          {date && <div className={s.date}>{moment(date).format("D MMM YYYY")}</div>}
          <Link href={`/${category.slug}`} className={s.category}>
            <SpriteIcon icon={IconTurnDownRight} />
            <span>{category.title}</span>
          </Link>
        </div>
        <Heading level={4} tag={headingTag || 3} className={s.heading} html={displayHeading} />
        <div className={s.excerpt}>{excerpt}</div>
      </div>
      <div className={s.meta}>
        <p>
          Words: <strong>{words}</strong>
        </p>
        <p>
          Pages: <strong>{pages}</strong>
        </p>
        <p>
          Views: <strong>{views}</strong>
        </p>
      </div>
      <Link href={`/${slug}`} className={s.link} />
    </div>
  );
};
